import { Star, Check, Lock, ShieldCheck, ChevronDown, X, MapPin, Search } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import doctorTabletTeal from "@/assets/showcase/doctor-tablet-teal.png";
import doctorTabletMale from "@/assets/showcase/doctor-tablet-male.png";
import doctorLaptopGlasses from "@/assets/showcase/doctor-laptop-glasses.png";
import patientPhone from "@/assets/showcase/patient-phone-smile.png";
import staffLaptop from "@/assets/showcase/staff-laptop-peach.png";

/**
 * Tebra-style portrait + floating UI card showcase.
 * Deep teal / coral palette, pure white background, rounded soft cards.
 * Used across landing, marketing shells, feature pages and AI module pages.
 */

export type ShowcaseScene =
  | "reviews"
  | "billing"
  | "analytics"
  | "prescribe"
  | "portal"
  | "presence"
  | "patient-experience"
  | "prescription-tracker";

export interface PortraitShowcaseProps {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  scene?: ShowcaseScene;
  portrait?: "doctor-female" | "doctor-male" | "doctor-glasses" | "patient" | "staff";
  reversed?: boolean;
}

const PORTRAITS = {
  "doctor-female": doctorTabletTeal,
  "doctor-male": doctorTabletMale,
  "doctor-glasses": doctorLaptopGlasses,
  patient: patientPhone,
  staff: staffLaptop,
};

// Deep teal + coral system, kept literal to match the reference exactly.
const TEAL = "#0c4a4d";
const TEAL_SOFT = "#d9e8e6";
const CORAL = "#f97362";
const CORAL_SOFT = "#fde4de";
const INK = "#0f172a";

function autoScene(seed: string): ShowcaseScene {
  const scenes: ShowcaseScene[] = [
    "reviews",
    "billing",
    "analytics",
    "prescribe",
    "portal",
    "presence",
    "patient-experience",
    "prescription-tracker",
  ];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return scenes[h % scenes.length];
}

function autoPortrait(seed: string): PortraitShowcaseProps["portrait"] {
  const list: PortraitShowcaseProps["portrait"][] = [
    "doctor-female",
    "doctor-male",
    "doctor-glasses",
    "patient",
    "staff",
  ];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 33 + seed.charCodeAt(i)) >>> 0;
  return list[h % list.length];
}

/* ---------- Scene renderers ---------- */

function ReviewsScene() {
  return (
    <>
      <div className="absolute left-0 top-4 md:left-6 md:top-8 w-[280px] md:w-[340px] rounded-2xl bg-white shadow-[0_18px_50px_-18px_rgba(15,23,42,0.25)] ring-1 ring-black/5 p-4 space-y-3">
        {[
          { name: "Olivia R.", txt: "Dr. Shakti has been our pediatrician for over five years, warm and attentive.", ago: "left 3 days ago" },
          { name: "Kevin J.", txt: "What I love most about Dr. Shakti is how she takes time during appointments.", ago: "left 3 days ago" },
        ].map((r) => (
          <div key={r.name} className="flex gap-3">
            <div className="h-9 w-9 rounded-full shrink-0" style={{ background: TEAL_SOFT }} />
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-[11px]">
                <span className="font-semibold" style={{ color: INK }}>{r.name}</span>
                <span className="text-slate-400">· {r.ago}</span>
              </div>
              <div className="flex gap-0.5 my-0.5">
                {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="w-3 h-3 fill-[#f59e0b] text-[#f59e0b]" />)}
              </div>
              <p className="text-[11px] leading-snug text-slate-600 line-clamp-2">{r.txt}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="absolute left-2 md:left-10 bottom-6 w-[300px] md:w-[380px] rounded-2xl bg-white shadow-[0_24px_60px_-22px_rgba(15,23,42,0.35)] ring-1 ring-black/5 p-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="h-8 w-8 rounded-full" style={{ background: TEAL_SOFT }} />
          <div>
            <div className="text-xs font-semibold" style={{ color: INK }}>Olivia Randall</div>
            <div className="text-[10px] text-slate-500">★★★★★ · Posted 2 weeks ago</div>
          </div>
        </div>
        <p className="text-[11px] text-slate-600 leading-snug mb-3">Dr. Shakti has been our pediatrician for over five years, and we couldn't be happier.</p>
        <div className="rounded-xl px-3 py-2 text-[11px] text-slate-700 shadow-inner" style={{ background: CORAL_SOFT, border: `1px solid ${CORAL}55` }}>
          Thank you, Olivia! Appreciate your kind words — see you next appointment.
        </div>
        <div className="mt-3 flex items-center gap-2 justify-end">
          <button className="text-[11px] px-3 py-1 rounded-md border border-slate-200 text-slate-600">Cancel</button>
          <button className="text-[11px] px-3 py-1 rounded-md text-white" style={{ background: TEAL }}>Reply</button>
        </div>
      </div>
    </>
  );
}

function BillingScene() {
  return (
    <>
      <div className="absolute left-0 top-6 md:left-4 w-[300px] md:w-[360px] rounded-2xl bg-white shadow-[0_18px_50px_-18px_rgba(15,23,42,0.25)] ring-1 ring-black/5 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-semibold" style={{ color: TEAL }}>Electronic claims</div>
          <X className="w-3.5 h-3.5 text-slate-400" />
        </div>
        <div className="grid grid-cols-2 gap-2 text-[10px] text-slate-500">
          <div><div>Start date</div><div className="mt-1 rounded-md bg-slate-100 px-2 py-1.5 flex justify-between"><span>Select</span><ChevronDown className="w-3 h-3" /></div></div>
          <div><div>End date</div><div className="mt-1 rounded-md bg-slate-100 px-2 py-1.5 flex justify-between"><span>Select</span><ChevronDown className="w-3 h-3" /></div></div>
          <div className="col-span-2"><div>All insurance companies</div><div className="mt-1 rounded-md bg-slate-100 px-2 py-1.5 flex justify-between"><span>Select</span><ChevronDown className="w-3 h-3" /></div></div>
        </div>
        <div className="mt-3 flex justify-end gap-2 text-[11px]">
          <span className="text-slate-500">Cancel</span>
          <span className="px-3 py-1 rounded-md text-white" style={{ background: INK }}>Submit</span>
        </div>
      </div>
      <div className="absolute left-4 md:left-16 bottom-10 w-[280px] md:w-[340px] rounded-2xl bg-white shadow-[0_24px_60px_-22px_rgba(15,23,42,0.35)] ring-1 ring-black/5 p-5 text-center">
        <p className="text-sm text-slate-600">There are <b className="text-slate-900">4 total claims</b> that are ready for electronic submission</p>
      </div>
    </>
  );
}

function AnalyticsScene() {
  return (
    <>
      <div className="absolute left-0 top-2 md:left-2 w-[220px] rounded-2xl bg-white shadow-[0_18px_50px_-18px_rgba(15,23,42,0.25)] ring-1 ring-black/5 p-4">
        <div className="text-xs font-semibold mb-1" style={{ color: TEAL }}>Encounters</div>
        <div className="text-[10px] text-slate-500">Total Encounters</div>
        <div className="text-2xl font-bold" style={{ color: INK }}>40,552</div>
        <svg viewBox="0 0 120 40" className="mt-2 w-full h-10">
          {[6, 12, 8, 20, 28, 34, 22, 30, 26, 18].map((h, i) => (
            <rect key={i} x={i * 12} y={40 - h} width={8} height={h} rx={2} fill={CORAL} opacity={0.85} />
          ))}
        </svg>
      </div>
      <div className="absolute left-6 md:left-10 bottom-10 w-[230px] rounded-2xl bg-white shadow-[0_24px_60px_-22px_rgba(15,23,42,0.35)] ring-1 ring-black/5 p-4">
        <div className="text-xs font-semibold mb-2" style={{ color: INK }}>Days Revenue Outstanding</div>
        <svg viewBox="0 0 120 70" className="w-full h-24">
          <path d="M10 60 A50 50 0 0 1 110 60" fill="none" stroke="#e2e8f0" strokeWidth="10" strokeLinecap="round" />
          <path d="M10 60 A50 50 0 0 1 78 21" fill="none" stroke={CORAL} strokeWidth="10" strokeLinecap="round" />
          <path d="M78 21 A50 50 0 0 1 110 60" fill="none" stroke={TEAL} strokeWidth="10" strokeLinecap="round" />
          <text x="60" y="55" textAnchor="middle" fontSize="18" fontWeight="700" fill={INK}>46.62</text>
        </svg>
      </div>
    </>
  );
}

function PrescribeScene() {
  return (
    <>
      <div className="absolute left-0 top-6 md:left-4 w-[300px] md:w-[360px] rounded-2xl bg-white shadow-[0_18px_50px_-18px_rgba(15,23,42,0.25)] ring-1 ring-black/5 p-4">
        <div className="text-xs font-semibold mb-3" style={{ color: TEAL }}>Prescribe medication</div>
        {[
          ["Drug", "Amoxicillin 250 mg capsule"],
          ["Patient instructions", "1 cap(s) two times a day x10 days"],
        ].map(([l, v]) => (
          <div key={l} className="mb-2">
            <div className="text-[10px] text-slate-500">{l}</div>
            <div className="mt-0.5 rounded-md bg-slate-100 px-2 py-1.5 text-[11px] text-slate-700">{v}</div>
          </div>
        ))}
        <div className="grid grid-cols-2 gap-2 text-[10px] text-slate-500">
          <div><div>Quantity</div><div className="mt-0.5 rounded-md bg-slate-100 px-2 py-1.5 flex justify-between"><span>20</span><ChevronDown className="w-3 h-3" /></div></div>
          <div><div>Days supply</div><div className="mt-0.5 rounded-md bg-slate-100 px-2 py-1.5">10</div></div>
        </div>
        <div className="mt-3 flex justify-end gap-2 text-[11px]">
          <span className="text-slate-500">Cancel</span>
          <span className="px-3 py-1 rounded-full text-white" style={{ background: INK }}>Create</span>
        </div>
      </div>
      <div className="absolute left-4 md:left-12 bottom-14 rounded-xl px-3 py-2 text-white text-[11px] font-semibold shadow-lg flex items-center gap-2" style={{ background: TEAL }}>
        <Check className="w-3.5 h-3.5" /> Order created for Olivia Randall
      </div>
    </>
  );
}

function PortalScene() {
  return (
    <>
      <div className="absolute left-0 top-0 md:left-2 rounded-full px-3 py-1.5 text-white text-[11px] font-semibold shadow-lg inline-flex items-center gap-1.5" style={{ background: TEAL }}>
        <Lock className="w-3 h-3" /> Connection is secure
      </div>
      <div className="absolute left-6 md:left-8 top-12 rounded-full px-3 py-1.5 text-white text-[11px] font-semibold shadow-lg inline-flex items-center gap-1.5" style={{ background: CORAL }}>
        <ShieldCheck className="w-3 h-3" /> HIPAA Compliant
      </div>
      <div className="absolute left-4 md:left-10 top-24 w-[240px] rounded-2xl bg-white shadow-[0_18px_50px_-18px_rgba(15,23,42,0.25)] ring-1 ring-black/5 p-4">
        <div className="text-xs font-semibold" style={{ color: INK }}>Health records</div>
        <p className="text-[11px] text-slate-500 mt-1 leading-snug">With Patient Portal, you can view your health information.</p>
        <button className="mt-2 rounded-md px-3 py-1.5 text-[11px] font-semibold text-white" style={{ background: INK }}>View health records</button>
      </div>
      <div className="absolute left-0 md:left-4 bottom-10 w-[240px] rounded-2xl bg-white shadow-[0_24px_60px_-22px_rgba(15,23,42,0.35)] ring-1 ring-black/5 p-4">
        <div className="text-xs font-semibold" style={{ color: INK }}>Documents</div>
        <p className="text-[11px] text-slate-500 mt-1 leading-snug">View documents shared with you and your provider.</p>
        <button className="mt-2 rounded-md px-3 py-1.5 text-[11px] font-semibold text-white" style={{ background: INK }}>Submit</button>
      </div>
    </>
  );
}

function PresenceScene() {
  return (
    <div className="absolute left-0 top-6 md:left-4 w-[360px] md:w-[420px] rounded-2xl bg-white shadow-[0_24px_60px_-22px_rgba(15,23,42,0.35)] ring-1 ring-black/5 overflow-hidden">
      <div className="h-8" style={{ background: TEAL }} />
      <div className="p-4">
        <div className="text-xs font-semibold" style={{ color: TEAL }}>Online Presence — Web Profiles</div>
        {["Dr. Ayesha Rahman", "Dr. Imran Kabir"].map((n, i) => (
          <div key={n} className="mt-3 flex items-center gap-3">
            <div className="h-9 w-9 rounded-full shrink-0" style={{ background: TEAL_SOFT }} />
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold" style={{ color: INK }}>{n}</div>
              <div className="text-[10px] text-slate-500">Medical Practice · Dhaka</div>
              <div className="text-[10px] text-slate-400 inline-flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> Change Location</div>
            </div>
            <span className="rounded-full px-2.5 py-1 text-[10px] font-semibold text-white" style={{ background: i === 0 ? CORAL : TEAL }}>
              {i === 0 ? "Show Details" : "Hide Details"}
            </span>
          </div>
        ))}
        <div className="mt-3 rounded-lg border border-slate-200 overflow-hidden">
          <div className="grid grid-cols-3 text-[10px] px-3 py-1.5 bg-slate-50 text-slate-500 font-semibold">
            <span>Site</span><span>Status</span><span className="text-right">Link</span>
          </div>
          {["Google", "Facebook", "Apple Maps", "Bing"].map((s, i) => (
            <div key={s} className="grid grid-cols-3 text-[11px] px-3 py-1.5 border-t border-slate-100 items-center">
              <span className="text-slate-700">{s}</span>
              <span style={{ color: i === 1 ? "#64748b" : TEAL }}>{i === 1 ? "Submitted" : "Live"}</span>
              <span className="text-right text-slate-500 underline">View Site</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PatientExperienceScene() {
  return (
    <>
      <div className="absolute left-0 top-4 md:left-2 w-[280px] rounded-2xl bg-white shadow-[0_18px_50px_-18px_rgba(15,23,42,0.25)] ring-1 ring-black/5 p-4">
        <div className="text-xs font-semibold" style={{ color: INK }}>Reputation overview</div>
        <div className="mt-1 text-3xl font-bold" style={{ color: INK }}>4.9<span className="text-lg text-slate-400">/5</span></div>
        <div className="flex gap-0.5 mt-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} className="w-4 h-4 fill-[#f59e0b] text-[#f59e0b]" />)}</div>
        <div className="text-[11px] text-slate-500 mt-1"><b className="text-slate-800">+2,829</b> total reviews</div>
      </div>
      <div className="absolute left-4 md:left-8 top-52 w-[160px] rounded-2xl bg-white shadow-[0_18px_50px_-18px_rgba(15,23,42,0.25)] ring-1 ring-black/5 p-4 text-center">
        <div className="mx-auto h-10 w-10 rounded-full flex items-center justify-center text-lg" style={{ background: TEAL_SOFT, color: TEAL }}>☺</div>
        <div className="mt-1 text-2xl font-bold" style={{ color: INK }}>95%</div>
        <div className="text-[11px] text-slate-500">2687</div>
      </div>
      <div className="absolute left-0 md:left-6 bottom-10 w-[240px] rounded-2xl bg-white shadow-[0_24px_60px_-22px_rgba(15,23,42,0.35)] ring-1 ring-black/5 p-4">
        <div className="text-xs font-semibold" style={{ color: INK }}>About you</div>
        <div className="mt-2 flex items-center gap-2">
          <div className="h-9 w-9 rounded-full" style={{ background: TEAL_SOFT }} />
          <div>
            <div className="text-[10px] text-slate-500">Name</div>
            <div className="text-xs font-semibold" style={{ color: INK }}>Ana Diaz</div>
          </div>
        </div>
        <div className="mt-2 text-[10px] text-slate-500">Date of birth</div>
        <div className="text-xs font-semibold" style={{ color: INK }}>10/10/2000</div>
      </div>
    </>
  );
}

function PrescriptionTrackerScene() {
  return (
    <>
      <div className="absolute left-0 top-4 md:left-2 w-[240px] rounded-2xl bg-white shadow-[0_18px_50px_-18px_rgba(15,23,42,0.25)] ring-1 ring-black/5 p-4">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold" style={{ color: INK }}>Prescribers</div>
          <Search className="w-3.5 h-3.5 text-slate-400" />
        </div>
        {["Olivia Randall", "Hank Mardukas", "Eliana Kortiva"].map((n) => (
          <div key={n} className="mt-2 flex items-center gap-2 text-[11px]" style={{ color: TEAL }}>
            <div className="w-3.5 h-3.5 rounded-sm border" style={{ borderColor: TEAL }} />
            <Check className="w-3 h-3 -ml-5" />
            <span className="text-slate-700 ml-3">{n}</span>
          </div>
        ))}
      </div>
      {[
        { name: "Olivia Randal", date: "06/06/2026", med: "Lasix 20mg oral tablet", tag: "New", color: TEAL_SOFT, ink: TEAL },
        { name: "Hank Mardukas", date: "05/24/2026", med: "Lisinopril 20mg oral tablet", tag: "Pending", color: "#fef3c7", ink: "#b45309" },
        { name: "Eliana Kortiva", date: "03/16/2026", med: "Metformin 500mg oral tablet", tag: "Expired", color: CORAL_SOFT, ink: CORAL },
      ].map((r, i) => (
        <div
          key={r.name}
          className="absolute w-[240px] rounded-2xl bg-white shadow-[0_18px_50px_-18px_rgba(15,23,42,0.25)] ring-1 ring-black/5 p-3"
          style={{ left: 20 + i * 12, bottom: 20 + i * 90 }}
        >
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full" style={{ background: TEAL_SOFT }} />
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold truncate" style={{ color: INK }}>{r.name}</div>
              <div className="text-[10px] text-slate-500">{r.date}</div>
            </div>
            <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold" style={{ background: r.color, color: r.ink }}>{r.tag}</span>
          </div>
          <div className="mt-1.5 text-[11px] text-slate-600">{r.med}</div>
        </div>
      ))}
    </>
  );
}

const SCENES: Record<ShowcaseScene, () => JSX.Element> = {
  reviews: ReviewsScene,
  billing: BillingScene,
  analytics: AnalyticsScene,
  prescribe: PrescribeScene,
  portal: PortalScene,
  presence: PresenceScene,
  "patient-experience": PatientExperienceScene,
  "prescription-tracker": PrescriptionTrackerScene,
};

const SCENE_HINTS: Record<string, ShowcaseScene> = {
  reviews: "reviews",
  nps: "reviews",
  billing: "billing",
  analytics: "analytics",
  prescriptions: "prescribe",
  "medicine-library": "prescribe",
  "patient-portal": "portal",
  compliance: "portal",
  "marketing-suite": "presence",
  campaigns: "presence",
  patients: "patient-experience",
  "care-gaps": "patient-experience",
  appointments: "prescription-tracker",
  chambers: "prescription-tracker",
};

/** Stand-alone portrait + floating-cards stage. Use inside custom hero layouts. */
export function PortraitStage({
  scene,
  portrait,
  className = "",
}: {
  scene: ShowcaseScene;
  portrait: NonNullable<PortraitShowcaseProps["portrait"]>;
  className?: string;
}) {
  const Scene = SCENES[scene];
  const src = PORTRAITS[portrait];
  return (
    <div className={`relative h-[460px] md:h-[540px] w-full ${className}`}>
      {/* Floating cards live in the left ~55% only, so they never cross the face */}
      <div className="absolute inset-y-0 left-0 w-[58%] z-10 pointer-events-none">
        <Scene />
      </div>
      {/* Portrait pinned to the right, on top of card edges but clear of the face zone */}
      <img
        src={src}
        alt=""
        loading="lazy"
        className="absolute right-0 bottom-0 h-full w-auto object-contain object-bottom select-none pointer-events-none z-20"
        draggable={false}
      />
    </div>
  );
}


export default function PortraitShowcase({
  eyebrow,
  title,
  subtitle,
  scene,
  portrait,
  reversed = false,
}: PortraitShowcaseProps) {
  const { lang } = useLang();
  const seed = `${title}${scene ?? ""}`;
  const activeScene = scene ?? SCENE_HINTS[seed.toLowerCase()] ?? autoScene(seed);
  const activePortrait = portrait ?? autoPortrait(seed);
  const Scene = SCENES[activeScene];
  const portraitSrc = PORTRAITS[activePortrait!];

  return (
    <section className="relative bg-white py-16 md:py-24 overflow-hidden">
      <div className="max-w-6xl mx-auto px-6">
        <div className={`grid gap-8 md:gap-12 items-center ${reversed ? "md:grid-cols-[1fr_1.05fr]" : "md:grid-cols-[1.05fr_1fr]"}`}>
          {/* Copy */}
          <div className={reversed ? "md:order-2" : ""}>
            {eyebrow && (
              <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] px-3 py-1.5 rounded-full mb-4" style={{ color: TEAL, background: TEAL_SOFT }}>
                {eyebrow}
              </div>
            )}
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight leading-[1.05]" style={{ color: INK, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" }}>
              {title}
            </h2>
            {subtitle && (
              <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed max-w-xl">{subtitle}</p>
            )}
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="/auth" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white" style={{ background: INK }}>
                {lang === "bn" ? "শুরু করুন" : "Get started"}
              </a>
              <a href="/features" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold border" style={{ color: TEAL, borderColor: `${TEAL}55` }}>
                {lang === "bn" ? "ট্যুর দেখুন" : "Take a tour"}
              </a>
            </div>
          </div>

          {/* Portrait + floating cards stage */}
          <div className={`relative h-[520px] md:h-[560px] ${reversed ? "md:order-1" : ""}`}>
            {/* Cards constrained to left ~60% so they stay creative but never cross the face */}
            <div className="absolute inset-y-0 left-0 w-[60%] z-10 pointer-events-none">
              <Scene />
            </div>
            <img
              src={portraitSrc}
              alt=""
              loading="lazy"
              width={1024}
              height={1200}
              className="absolute right-0 bottom-0 h-full w-auto object-contain object-bottom select-none pointer-events-none z-20"
              draggable={false}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
